import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { GlobalConstants } from '@globalConstants';
import { IFormErrors } from '@interfaces/IFormsErrors';
import { IPeriod } from '@interfaces/IPeriod';
import { IAppState } from '@myStore/app.state';
import { IGroupFormErrors } from '@myStore/groups/groups.state';
import {
  AddHolidaysToPeriodRequest,
  ClearUpdatePeriodErrors,
} from '@myStore/periods-changes/periods-changes.actions';
import {
  selectPeriodChangesFailure,
  selectPeriodChangesLoading,
  selectPeriodChangesSuccess,
} from '@myStore/periods-changes/periods-changes.selector';
import {
  ClearGroupPeriods,
  GetGroupPeriodsRequest,
  GetPeriodRequest,
} from '@myStore/periods-view/periods-view.actions';
import { selectPeriod } from '@myStore/periods-view/periods-view.selector';
import { select, Store } from '@ngrx/store';
import { UIThemeService } from '@services/UITheme/UITheme.service';
import { CustomDateAdapter } from '@shared/adapter/date.adapter';
import { FormDialogComponent } from '@shared/components/UI/form-dialog/form-dialog.component';
import { formatDatePicker } from '@shared/utils/dateUtils';
import { isControlsIsValid } from '@shared/utils/formUtils';
import { MyErrorStateMatcher } from '@validators/matcher';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-holiday-form',
  templateUrl: './holiday-form.component.html',
  styleUrls: ['./holiday-form.component.sass'],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: GlobalConstants.DEFAULT_DATEPICKER_FORMAT,
    },
  ],
})
export class HolidayFormComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public sendLoading$: Observable<boolean> = this._store.pipe(
    select(selectPeriodChangesLoading)
  );
  public errors$: IFormErrors | null = null;
  public period$: IPeriod | null = null;
  public matcher = new MyErrorStateMatcher();
  public dialogData = this._dialogRef.componentInstance.data;
  public requiredErrorMessage = 'Поле является обазательным';
  public holidayForm = new UntypedFormGroup({
    offset_date: new UntypedFormControl(GlobalConstants.dateFormat),
    weeks_offset: new UntypedFormControl(1),
  });
  public periodId!: number;
  public groupId!: number;

  constructor(
    private _store: Store<IAppState>,
    private _dialogRef: MatDialogRef<FormDialogComponent>,
    public UIThemeService: UIThemeService
  ) {}

  ngOnInit(): void {
    if (this.dialogData.groupId) {
      this.groupId = this.dialogData.groupId;

      if (this.dialogData.itemId) {
        this.periodId = this.dialogData.itemId;

        this._store.dispatch(new GetPeriodRequest(this.periodId));
      }
    }

    this._store
      .pipe(select(selectPeriodChangesSuccess), takeUntil(this.destroy$))
      .subscribe(success => {
        if (success) {
          this.onCancel();
          this._store.dispatch(new ClearGroupPeriods());
          this._store.dispatch(new GetGroupPeriodsRequest(this.groupId));
        }
      });

    this._store
      .pipe(select(selectPeriod), takeUntil(this.destroy$))
      .subscribe(period => {
        this.period$ = period;
      });

    this._store
      .pipe(select(selectPeriodChangesFailure), takeUntil(this.destroy$))
      .subscribe(errors => {
        this.errors$ = errors;
        this.setControlErrors(errors);
      });
  }

  onSubmit(): void {
    const formData = this.holidayForm.value;
    let offsetDate = formData.offset_date;

    if (offsetDate && offsetDate._i) {
      offsetDate = formatDatePicker(offsetDate._i);
    }

    if (isControlsIsValid(this.holidayForm.controls)) {
      if (
        this.period$ &&
        this.period$.lesson_days &&
        (this.period$.webinar_days || this.period$.is_esdp)
      ) {
        const holidayData = {
          ...formData,
          lesson_days: this.period$.lesson_days.numbers,
          offset_date: offsetDate,
          is_offset: true,
        };

        if (this.period$.webinar_days) {
          holidayData.webinar_days = this.period$.webinar_days.numbers;
        }

        this._store.dispatch(
          new AddHolidaysToPeriodRequest(this.periodId, holidayData)
        );
      }
    }
  }

  onCancel() {
    this._dialogRef.close();
  }

  get offsetDate() {
    return this.holidayForm.get('offset_date');
  }

  get offsetWeeks() {
    return this.holidayForm.get('weeks_offset');
  }

  getMessageFormError(name: string): string | null {
    if (this.errors$ && this.errors$[name]) {
      return this.errors$[name][0];
    }

    return null;
  }

  setControlErrors(errors: IGroupFormErrors | null): void {
    if (errors) {
      Object.keys(errors).forEach(key => {
        if (this.holidayForm.controls[key]) {
          this.holidayForm.controls[key].setErrors({ errors: errors[key] });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this._store.dispatch(new ClearUpdatePeriodErrors());
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
